import { useEffect, useState } from "react";
import { FolderSync, CheckCircle2, XCircle, RefreshCw, AlertTriangle, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";

export default function AdminDropboxSync() {
  const { authAxios } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        authAxios.get("/dropbox/status"),
        authAxios.get("/dropbox/recent-logs?limit=30"),
      ]);
      setStatus(s.data);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error("Errore caricamento stato Dropbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    const tid = toast.loading("Sincronizzazione Dropbox in corso (può richiedere fino a 2 minuti per file pesanti)...");
    try {
      // Timeout esteso a 5 minuti: i PDF estratto conto possono richiedere
      // 60-90s di parsing. Senza questo override, axios aborterebbe la
      // richiesta dopo ~30s mostrando un falso errore mentre il backend
      // continua a importare correttamente in background.
      const r = await authAxios.post("/dropbox/sync-now", null, { timeout: 5 * 60 * 1000 });
      const total = (r.data.sent || 0) + (r.data.failed || 0);
      if (total === 0) {
        toast.message("Nessun file nuovo da importare", { id: tid });
      } else if ((r.data.failed || 0) === 0) {
        toast.success(`Importati ${r.data.sent} file`, { id: tid });
      } else {
        toast.warning(`Importati ${r.data.sent}, falliti ${r.data.failed}`, { id: tid, duration: 6000 });
      }
      await refresh();
    } catch (e) {
      // Caso speciale: timeout/network error → il backend probabilmente sta ancora elaborando.
      // Diciamo all'utente di aspettare e ricaricare invece di mostrare "errore" fuorviante.
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const isNet = !e?.response;
      if (isTimeout || isNet) {
        toast.warning(
          "Il file è grande e l'importazione sta ancora girando in background. Attendi 1-2 minuti e premi 'Aggiorna' per vedere i risultati.",
          { id: tid, duration: 9000 }
        );
        // Auto-refresh dopo 30s per tentare di catturare il risultato finale
        setTimeout(() => { refresh().catch(() => {}); }, 30000);
      } else {
        toast.error(e.response?.data?.detail || "Errore sync", { id: tid });
      }
    } finally { setSyncing(false); }
  };

  const fmtDT = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-dropbox-sync-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <PageHeader title="Dropbox Sync" />
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Sincronizzazione automatica Dropbox</h1>
            <p className="text-zinc-400 text-xs sm:text-sm">L'app controlla ogni 10 minuti le cartelle e importa i file nuovi</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={refresh} variant="ghost" size="sm" disabled={loading} className="text-zinc-400 hover:text-white" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
            <Button onClick={syncNow} disabled={syncing || !status?.configured} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="sync-now-btn">
              <FolderSync className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync ora
            </Button>
          </div>
        </div>

        {/* Status */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              Configurazione
              {status?.configured ? (
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Attivo</Badge>
              ) : (
                <Badge className="bg-red-500/15 text-red-300 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Non configurato</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1 text-zinc-300">
            <div className="flex justify-between"><span className="text-zinc-400">Cartella radice</span><span className="font-mono">{status?.root_folder}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Frequenza poll</span><span>{status?.poll_interval_seconds}s ({Math.round((status?.poll_interval_seconds||600)/60)} min)</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Ultimo sync</span><span>{fmtDT(status?.last_run)}</span></div>
          </CardContent>
        </Card>

        {/* Pending files per folder */}
        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2"><Inbox className="w-4 h-4 text-blue-400" /> File in attesa</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(status?.watched_folders || []).map((f) => {
              const n = status?.pending_files?.[f] ?? 0;
              return (
                <div key={f} className="rounded-lg border border-white/5 bg-[#0f0f11] p-3" data-testid={`folder-${f}`}>
                  <p className="text-zinc-400 text-[11px] uppercase tracking-wider">{f}</p>
                  <p className={`text-2xl font-mono font-bold mt-0.5 ${n > 0 ? "text-amber-300" : "text-zinc-600"}`}>{n < 0 ? "—" : n}</p>
                  <p className="text-zinc-600 text-[10px]">{n === 0 ? "vuota" : n === 1 ? "file pronto" : "file pronti"}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Recent logs */}
        <Card className="bg-[#18181b] border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Cronologia import</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {logs.length === 0 ? (
              <p className="px-4 py-6 text-zinc-500 text-sm text-center">Nessun import effettuato</p>
            ) : (
              <ul className="divide-y divide-white/5" data-testid="logs-list">
                {logs.map((log, i) => (
                  <li key={`${log.ts || ""}-${log.folder || ""}-${log.filename || ""}-${i}`} className="px-4 py-3 flex items-start gap-3" data-testid={`log-${i}`}>
                    <div className="shrink-0 mt-0.5">
                      {log.status === "done"
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        : <AlertTriangle className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={log.status === "done" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]" : "bg-red-500/15 text-red-300 border-red-500/30 text-[10px]"}>
                          {log.folder}
                        </Badge>
                        <span className="text-white text-sm truncate">{log.filename}</span>
                      </div>
                      {log.status === "done" && log.result_summary && (
                        <p className="text-emerald-400/80 text-[11px] mt-0.5">{log.result_summary}</p>
                      )}
                      {log.status === "failed" && log.error && (
                        <p className="text-red-400/80 text-[11px] mt-0.5 break-words">{log.error}</p>
                      )}
                      <p className="text-zinc-500 text-[10px] mt-0.5">{fmtDT(log.ts)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <p className="text-zinc-500 text-[11px] mt-4 text-center">
          Suggerimento: i file con errori finiscono in <span className="font-mono text-zinc-400">/Triolo.Store-Sync/_Errors</span> con un file <span className="font-mono">.error.txt</span> a fianco.
        </p>
      </main>
    </div>
  );
}
